import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Driver } from '@/core/domain/entities/Driver'
import {
  IRideService,
  IDriverService,
  ISolicitationService,
} from '@/core/application/services/interfaces'
import { container, TYPES } from '@/core/container'
import { isOk } from '@/core/shared/utils/Result'
import { useAuth } from '@/presentation/hooks/useAuth'
import {
  Period,
  UserId,
  DashboardSummary,
  SolicitationMetrics,
} from '@/core/shared/types'
import { getPeriodDateRange } from '@/core/shared'
import { useDashboardStore } from '@/core/stores/dashboardStore'

export const useDashboard = () => {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const { selectedPeriod, setSelectedPeriod, customDateRange, setCustomDateRange } =
    useDashboardStore()

  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [drivers, setDrivers] = useState<Driver[] | null>(null)
  const [solicitations, setSolicitations] =
    useState<SolicitationMetrics | null>(null)

  const [isLoadingRides, setIsLoadingRides] = useState(false)
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false)
  const [isLoadingSolicitations, setIsLoadingSolicitations] = useState(false)

  const [ridesError, setRidesError] = useState<Error | null>(null)
  const [driversError, setDriversError] = useState<Error | null>(null)
  const [solicitationsError, setSolicitationsError] = useState<Error | null>(
    null
  )

  const rideService = container.resolve<IRideService>(TYPES.RideService)
  const driverService = container.resolve<IDriverService>(TYPES.DriverService)
  const solicitationService = container.resolve<ISolicitationService>(
    TYPES.SolicitationService
  )

  useEffect(() => {
    const periodFromUrl = searchParams.get('period') as Period | null
    if (periodFromUrl && periodFromUrl !== selectedPeriod) {
      setSelectedPeriod(periodFromUrl)
    }
  }, [searchParams, selectedPeriod, setSelectedPeriod])

  const fetchRidesData = useCallback(
    async (userId: UserId, period: Period) => {
      if (!userId) {
        console.warn('No userId provided for dashboard data fetch')
        return
      }

      setIsLoadingRides(true)
      setRidesError(null)

      const controller = new AbortController()
      const timeoutId = setTimeout(() => {
        controller.abort()
      }, 30000)

      try {
        const dateRange = getPeriodDateRange(
          period,
          customDateRange ?? undefined
        )
        const dashboardResult = await rideService.getDashboardData({
          userId,
          dateStart: dateRange.start,
          dateEnd: dateRange.end,
        })

        clearTimeout(timeoutId)

        if (!isOk(dashboardResult)) {
          throw new Error(dashboardResult.error.message)
        }

        const data = dashboardResult.value
        const summaryData: DashboardSummary = {
          totalDrivers: data.driverMetrics.totalDrivers || 0,
          onlineDrivers: data.driverMetrics.onlineDrivers || 0,
          offlineDrivers: data.driverMetrics.offlineDrivers || 0,
          averageRating: data.driverMetrics.averageRating || 0,
          onlinePercentage: data.driverMetrics.onlinePercentage || 0,
          totalRides: data.rideMetrics.totalRides || 0,
          canceledRides: data.rideMetrics.canceledRides || 0,
          totalEarnings: data.rideMetrics.formattedTotalRevenue || 'R$ 0,00',
          subscription: data.driverMetrics.subscription || 0,
          ridesPerDriver: data.rideMetrics.ridesPerDriver,
          topDriversByRides: data.rideMetrics.topDriversByRides,
          ridesPerDriverList: data.rideMetrics.ridesPerDriverList,
          lowDriversByRides: data.rideMetrics.lowDriversByRides,
          lastUpdatedAt: data.rideMetrics.lastUpdatedAt,
        }

        setSummary(summaryData)
      } catch (err) {
        clearTimeout(timeoutId)

        if (err instanceof Error && err.name === 'AbortError') {
          console.error('Timeout ao carregar corridas:', err)
          setRidesError(
            new Error('Tempo de espera excedido ao carregar corridas')
          )
        } else {
          const message =
            err instanceof Error ? err.message : 'Erro ao carregar corridas'
          console.error('Erro ao carregar corridas:', err)
          setRidesError(new Error(message))
        }
      } finally {
        setIsLoadingRides(false)
      }
    },
    [rideService, customDateRange]
  )

  const fetchDriversData = useCallback(
    async (userId: UserId) => {
      if (!userId) return

      setIsLoadingDrivers(true)
      setDriversError(null)

      const controller = new AbortController()
      const timeoutId = setTimeout(() => {
        controller.abort()
      }, 30000)

      try {
        const driversResult = await driverService.getDriverList({ userId })

        clearTimeout(timeoutId)

        if (!isOk(driversResult)) {
          throw new Error(driversResult.error.message)
        }

        setDrivers(driversResult.value)
      } catch (err) {
        clearTimeout(timeoutId)

        if (err instanceof Error && err.name === 'AbortError') {
          console.error('Timeout ao carregar motoristas:', err)
          setDriversError(
            new Error('Tempo de espera excedido ao carregar motoristas')
          )
        } else {
          const message =
            err instanceof Error ? err.message : 'Erro ao carregar motoristas'
          console.error('Erro ao carregar motoristas:', err)
          setDriversError(new Error(message))
        }
      } finally {
        setIsLoadingDrivers(false)
      }
    },
    [driverService]
  )

  const fetchSolicitationsData = useCallback(
    async (userId: UserId, period: Period) => {
      if (!userId) return

      setIsLoadingSolicitations(true)
      setSolicitationsError(null)

      const controller = new AbortController()
      const timeoutId = setTimeout(() => {
        controller.abort()
      }, 30000)

      try {
        const dateRange = getPeriodDateRange(
          period,
          customDateRange ?? undefined
        )
        const solicitationResult =
          await solicitationService.getSolicitationMetrics({
            userId,
            dateStart: dateRange.start,
            dateEnd: dateRange.end,
          })

        clearTimeout(timeoutId)

        if (!isOk(solicitationResult)) {
          throw new Error(solicitationResult.error.message)
        }

        const solicitationData = solicitationResult.value
        const solicitationsMetrics: SolicitationMetrics = {
          total: solicitationData.total || 0,
          accepted: solicitationData.accepted || 0,
          refused: solicitationData.refused || 0,
          canceled: solicitationData.canceled || 0,
          pending: solicitationData.pending || 0,
          lastUpdatedAt: solicitationData.lastUpdatedAt,
        }

        setSolicitations(solicitationsMetrics)
      } catch (err) {
        clearTimeout(timeoutId)

        if (err instanceof Error && err.name === 'AbortError') {
          console.error('Timeout ao carregar solicitações:', err)
          setSolicitationsError(
            new Error('Tempo de espera excedido ao carregar solicitações')
          )
        } else {
          const message =
            err instanceof Error ? err.message : 'Erro ao carregar solicitações'
          console.error('Erro ao carregar solicitações:', err)
          setSolicitationsError(new Error(message))
        }
      } finally {
        setIsLoadingSolicitations(false)
      }
    },
    [solicitationService, customDateRange]
  )

  useEffect(() => {
    if (user?.id) {
      fetchRidesData(user.id, selectedPeriod)
      fetchDriversData(user.id)
      fetchSolicitationsData(user.id, selectedPeriod)
    }
  }, [
    user?.id,
    selectedPeriod,
    fetchRidesData,
    fetchDriversData,
    fetchSolicitationsData,
  ])

  const handlePeriodChange = (newPeriod: Period, customDates?: { startDate: string; endDate: string }) => {
    setSelectedPeriod(newPeriod)
    if (customDates) {
      setCustomDateRange(customDates)
    }
  }

  const refreshDashboard = useCallback(async () => {
    if (user?.id) {
      fetchRidesData(user.id, selectedPeriod)
      fetchDriversData(user.id)
      fetchSolicitationsData(user.id, selectedPeriod)
    }
  }, [
    user?.id,
    selectedPeriod,
    fetchRidesData,
    fetchDriversData,
    fetchSolicitationsData,
  ])

  const isLoading = isLoadingRides || isLoadingDrivers || isLoadingSolicitations
  const hasAnyError = ridesError || driversError || solicitationsError
  const hasData = summary || drivers || solicitations

  return {
    summary,
    drivers,
    solicitations,
    selectedPeriod,
    isLoading,
    isLoadingRides,
    isLoadingDrivers,
    isLoadingSolicitations,
    error: hasAnyError,
    ridesError,
    driversError,
    solicitationsError,
    handlePeriodChange,
    refreshDashboard,
    hasData: !!hasData,
  }
}
